import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  IconButton,
  Paper,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import PreviewIcon from '@mui/icons-material/Preview';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ReportPreview from './ReportPreview';
import { useNotification } from '../contexts/NotificationContext';
import { getReportHistoryStatus, getReportHistoryStatusDescription } from '../utils/reportHistoryStatus';
import { parseReportPdfFileNames } from '../utils/reportHistoryFiles';
import { getScheduledReportsNextRuns } from '../utils/reportSchedule';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';

const ReportHistory = ({ open, onClose, reportId, savedDashboards = [], isAuthenticated, authToken, managerId, adminId }) => {
  const { showSuccess, showError, showWarning } = useNotification();
  const [scheduleNow, setScheduleNow] = useState(() => new Date());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const [runLogExpanded, setRunLogExpanded] = useState(false);

  // Helper para obtener headers de autenticación
  const getAuthHeaders = () => {
    const headers = {
      'Authorization': authToken
    };
    if (adminId) {
      headers['X-Admin-Id'] = adminId;
    } else if (managerId) {
      headers['X-Manager-Id'] = managerId;
    }
    return headers;
  };

  const canLoad = Boolean(isAuthenticated && authToken && (managerId || adminId));

  const historyQuery = useQuery({
    queryKey: ['reportHistory', managerId, adminId, reportId || null],
    queryFn: async () => {
      const res = await axios.get('/api/reports/history', {
        headers: getAuthHeaders(),
        params: reportId ? { reportId } : {},
      });
      if (!res.data.success) throw new Error('No se pudo cargar el historial');
      return res.data.history || [];
    },
    enabled: open && canLoad,
  });
  const history = useMemo(() => historyQuery.data || [], [historyQuery.data]);

  // Misma queryKey que ReportManager.js: comparten la cache de react-query, asi
  // no hace falta volver a pedir la lista de reportes si ya se cargo ahi.
  const reportsListQuery = useQuery({
    queryKey: ['reports', managerId, adminId],
    queryFn: async () => {
      const res = await axios.get('/api/reports/list', { headers: getAuthHeaders() });
      if (!res.data.success) throw new Error('No se pudieron cargar los reportes');
      return {
        reports: (res.data.reports || []).filter(Boolean),
        dashboards: (res.data.dashboards || []).filter(Boolean),
      };
    },
    enabled: open && canLoad,
  });
  const reports = useMemo(() => reportsListQuery.data?.reports || [], [reportsListQuery.data]);

  const runLogQuery = useQuery({
    queryKey: ['reportRunLog', managerId, adminId],
    queryFn: async () => {
      const res = await axios.get('/api/reports/run-log', { headers: getAuthHeaders(), params: { lines: 60 } });
      if (!res.data.success) throw new Error('No se pudo cargar el log');
      return { lines: res.data.lines || [], logFile: res.data.logFile || '' };
    },
    enabled: runLogExpanded && canLoad,
  });
  const runLogLines = runLogQuery.data?.lines || [];
  const runLogPath = runLogQuery.data?.logFile || '';

  const scheduledNextRuns = useMemo(
    () => getScheduledReportsNextRuns(reports, scheduleNow, reportId || null),
    [reports, scheduleNow, reportId]
  );

  useEffect(() => {
    if (!open) setRunLogExpanded(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setScheduleNow(new Date());
    const id = setInterval(() => setScheduleNow(new Date()), 30000);
    return () => clearInterval(id);
  }, [open, reports]);

  const loadData = () => Promise.all([historyQuery.refetch(), reportsListQuery.refetch()]);

  const refreshAll = async () => {
    await loadData();
    if (runLogExpanded) {
      await runLogQuery.refetch();
    }
  };

  const handleRunLogAccordionChange = (_event, expanded) => {
    setRunLogExpanded(expanded);
  };

  const deleteHistoryMutation = useMutation({
    mutationFn: (historyId) => axios.delete(`/api/reports/history/${historyId}`, { headers: getAuthHeaders() }),
    onSuccess: async () => {
      await historyQuery.refetch();
      showSuccess('Registro eliminado del historial');
    },
    onError: (error) => {
      console.error('Error al eliminar del historial:', error);
      showError('Error al eliminar del historial. Por favor, intenta de nuevo.');
    },
  });

  const handleDelete = (historyId) => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      showWarning('Debes estar autenticado para eliminar del historial');
      return;
    }
    if (window.confirm('¿Eliminar este registro del historial?')) {
      deleteHistoryMutation.mutate(historyId);
    }
  };

  const handlePreview = async (historyItem) => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      showWarning('Debes estar autenticado para previsualizar reportes');
      return;
    }
    try {
      const { data } = await reportsListQuery.refetch();
      const report = (data?.reports || []).find((r) => r.id === historyItem.reportId);
      if (report) {
        setPreviewReport(report);
        setPreviewOpen(true);
      } else {
        showWarning('No se pudo encontrar el reporte. Puede que haya sido eliminado.');
      }
    } catch (error) {
      console.error('Error al cargar reporte para previsualización:', error);
      showError('Error al cargar el reporte para previsualización');
    }
  };

  const downloadPdfFile = async (fileName) => {
    const response = await axios.get(`/api/reports/download/${encodeURIComponent(fileName)}`, {
      headers: getAuthHeaders(),
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownload = async (historyItem) => {
    const fileNames = parseReportPdfFileNames(historyItem.fileName);
    if (!fileNames.length) {
      showWarning('El PDF no está disponible para descargar');
      return;
    }

    try {
      for (let i = 0; i < fileNames.length; i += 1) {
        await downloadPdfFile(fileNames[i]);
        if (i < fileNames.length - 1) {
          await new Promise((r) => setTimeout(r, 400));
        }
      }
      showSuccess(
        fileNames.length === 1
          ? 'PDF descargado'
          : `${fileNames.length} PDF descargados (uno por dashboard)`
      );
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      showError('Error al descargar el PDF');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            Historial de Reportes Generados
          </Box>
          <IconButton onClick={refreshAll} title="Actualizar historial y log" size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {scheduledNextRuns.length > 0 && (
          <Box sx={{ mb: history.length > 0 ? 3 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <ScheduleIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={600}>
                Próximas ejecuciones programadas
              </Typography>
            </Box>
            {scheduledNextRuns.map((entry) => (
              <Paper key={entry.reportId} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Typography variant="subtitle2">{entry.reportName}</Typography>
                {entry.runs.map((run, index) => (
                  <Typography
                    key={`${entry.reportId}-${index}`}
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.75 }}
                  >
                    {entry.runs.length > 1 ? `${run.slotDescription}: ` : ''}
                    {run.relative} — {run.absolute}
                  </Typography>
                ))}
              </Paper>
            ))}
          </Box>
        )}

        {scheduledNextRuns.length > 0 && history.length > 0 && <Divider sx={{ mb: 2 }} />}

        {history.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: scheduledNextRuns.length > 0 ? 2 : 4 }}>
            {scheduledNextRuns.length > 0
              ? 'Aún no hay ejecuciones registradas en el historial.'
              : 'No hay reportes generados en el historial.'}
          </Typography>
        ) : (
          <List>
            {history.map((item) => {
              const status = getReportHistoryStatus(item);
              const statusDetail = getReportHistoryStatusDescription(item);
              const pdfFiles = parseReportPdfFileNames(item.fileName);
              return (
              <Paper key={item.id} sx={{ mb: 2, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">{item.reportName}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Generado: {formatDate(item.generatedAt)}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                      />
                      <Chip
                        label={item.type === 'automatic' ? 'Automático' : 'Manual'}
                        color={item.type === 'automatic' ? 'primary' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                      {item.scheduleInfo && (
                        <Chip
                          label={item.scheduleInfo}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {pdfFiles.length > 0 && (
                        <Chip
                          label={
                            pdfFiles.length === 1
                              ? '1 PDF'
                              : `${pdfFiles.length} PDF (uno por dashboard)`
                          }
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    {statusDetail && (
                      <Typography
                        variant="body2"
                        color={status.color === 'error' ? 'error' : 'warning.main'}
                        sx={{ mt: 1 }}
                      >
                        {statusDetail}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handlePreview(item)}
                      title="Ver/Previsualizar Reporte"
                      sx={{ mr: 1 }}
                    >
                      <PreviewIcon />
                    </IconButton>
                    {pdfFiles.length > 0 && (
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(item)}
                        title={
                          pdfFiles.length === 1
                            ? 'Descargar PDF'
                            : `Descargar ${pdfFiles.length} PDF (uno por dashboard)`
                        }
                        sx={{ mr: 1 }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(item.id)}
                      color="error"
                      title="Eliminar"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            );
            })}
          </List>
        )}

        <Divider sx={{ my: 2 }} />
        <Accordion
          expanded={runLogExpanded}
          onChange={handleRunLogAccordionChange}
          disableGutters
          elevation={0}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Log de ejecuciones (servidor)</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button size="small" onClick={() => runLogQuery.refetch()}>Actualizar log</Button>
            </Box>
            {runLogPath && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Archivo: {runLogPath}
              </Typography>
            )}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                maxHeight: 220,
                overflow: 'auto',
                bgcolor: 'grey.50',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {runLogLines.length === 0
                ? 'Sin entradas aún. Ejecuta un reporte con ▶ y actualiza.'
                : runLogLines.join('\n')}
            </Paper>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      {/* Preview Dialog */}
      {previewReport && (
        <ReportPreview
          open={previewOpen}
          onClose={() => {
            setPreviewOpen(false);
            setPreviewReport(null);
          }}
          report={previewReport}
          savedDashboards={savedDashboards}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </Dialog>
  );
};

export default ReportHistory;
